import { QuarterlyReport } from "~/api/quarterly_reports"

/**
 * Centralized configuration for the Editor component in quarterly reports.
 *
 * This module provides utility functions to extract data from a QuarterlyReport
 * and a constant list of all available tools. All quarterly report editor pages
 * should use these utilities to ensure consistent tool availability.
 */

/**
 * All available editor tools for quarterly reports.
 * Every editor instance should have access to all tools.
 */
export const ALL_EDITOR_TOOLS = [
  // Menu tools
  "dataGeneratedContent",
  "aiContent",

  // Chart tools
  "chart",

  // AI content tools
  "attributionHighlights",
  "liquidityEvents",
  "newInvestments",
  "cashFlows",
  "highLevelSummary",

  // Performance tables
  "smallPerformanceByStageTable",
  "smallPerformanceBySeriesTable",
  "smallPerformanceByAssetClassTable",
  "smallPerformanceByVintageTable",
  "smallPerformanceByLpEntityTable",

  // Data visualization tools
  "topTenPortfolioCompaniesTable",
  "topFiveSeriesTable",
  "geographicExposureTable",
  "sectorExposureTable",
  "usMap",
  "portfolioCompanyLogos",
  "subSectorPieChart",

  // Historical performance tools
  "historicalPnLByVintageTable",
  "historicalPnLByAssetClassTable",
  "historicalIrrByAssetClassTable",
  "historicalIrrByVintageTable",
  "historicalPnLBySeriesTable",
  "historicalIrrBySeriesTable",
] as const

export type EditorToolName = (typeof ALL_EDITOR_TOOLS)[number]

/**
 * Extracts chartToolData from a QuarterlyReport for use with the Editor component.
 */
export function getChartToolData(report: QuarterlyReport | undefined) {
  if (!report) return {}

  const editorDataContent = report.section_content?.editor_data_content

  return {
    irrSeriesData: editorDataContent?.irr_series_data,
    topTenPortfolioCompaniesData:
      editorDataContent?.top_ten_portfolio_companies_data,
    topFiveSeriesData: editorDataContent?.top_five_series_data,
    geographicExposureData: editorDataContent?.geographic_exposure_data,
    sectorExposureData: editorDataContent?.sector_exposure_data,
    sp500DataAsOfDate: editorDataContent?.sp500_data_as_of_date,
    subSectorExposureData: editorDataContent?.sub_sector_exposure_data,
    portfolioCompanies: editorDataContent?.portfolio_companies,
    historicalPnLByVintageData:
      editorDataContent?.historical_pnl_by_vintage_data,
    historicalPnLByAssetClassData:
      editorDataContent?.historical_pnl_by_asset_class_data,
    historicalIrrByAssetClassData:
      editorDataContent?.historical_irr_by_asset_class_data,
    historicalIrrByVintageData:
      editorDataContent?.historical_irr_by_vintage_data,
    historicalPnLBySeriesData: editorDataContent?.historical_pnl_by_series_data,
    historicalIrrBySeriesData: editorDataContent?.historical_irr_by_series_data,
    performanceTableData: {
      stage: editorDataContent?.performance_by_stage_data,
      series: editorDataContent?.performance_by_series_data,
      assetClass: editorDataContent?.performance_by_asset_class_data,
      vintage: editorDataContent?.performance_by_vintage_data,
      lpEntity: editorDataContent?.performance_by_lp_entity_data,
    },
  }
}

/**
 * Extracts aiContentData from a QuarterlyReport for use with the Editor component.
 */
export function getAiContentData(report: QuarterlyReport | undefined) {
  if (!report) return {}

  const editorAiContent = report.section_content?.editor_ai_content

  return {
    attributionHighlights: editorAiContent?.attribution_highlights,
    liquidityEvents: editorAiContent?.liquidity_events,
    newInvestments: editorAiContent?.new_investments,
    cashFlows: editorAiContent?.cash_flows,
    highLevelSummary: editorAiContent?.high_level_summary,
  }
}
